import isNumber from 'lodash/isNumber';
import { Action, Dispatch } from 'redux';
import { Moment } from 'moment';
import { ThunkDispatch } from 'redux-thunk';

import exporter from '../exporter';
import {
  buildBasketPotential,
  createBasketFromBooking,
  createCleaningBasketFromBooking,
  updateAppointment as updateAppointmentUtil,
} from '../../utils/basket';
import * as bookingUtils from '../../utils/booking';
import { dispatchError, onFetchError, onFetchStart } from '../utils';
import { actionTypes } from '../actions';
import * as object from '../object';
import moment, { isWithin48Hours } from '../../utils/moment';
import * as api from '../../api/customer';
import * as userDuck from '../common/users';
import { isCleaningReference, isWellnessReference } from '../../utils/universes';
import { handlers as commonHandlers, initialState as commonInitialState, SET_ATTRIBUTE } from '../common/basket';
import { areExclusive, switchChoices } from '../../utils/previous-pros';
import { ErrorPayload, PayloadAction } from '../../types/state';
import { Booking, ProChoice, RecurrenceOrder } from '../../types/customer';
import { Config, ProChoiceValue, Universe, UniverseReference } from '../../types/commons';

import {
  getAllPrestationsInBasket,
  getAllProChoices,
  getBasketPrices,
  getMinimumAppointment,
  getPreviousBooking,
  getWantedProChoices,
} from './basket-selectors';
import {
  BasketState,
  PayloadPotential,
  CustomerRootState,
  OrderChangeRequest,
  PotentialCreation,
  PotentialUpdate,
  PotentialValidation,
} from './types';

const bookingMapper = <T>(booking: T) => ({
  ...booking,
  // TODO TI Booking: Remove this bookingMapper
  // @ts-ignore 'ends_at' only exists for childcare recurring potential and orders
  ends_at: booking.ends_at ? moment(booking.ends_at) : booking.ends_at,
});

// ~~~~~~~~~~ SELECTORS ~~~~~~~~~~

export const isRecurringCleaningSelector = (state: { basket: BasketState }) => {
  const { cleaningPrestation, universe } = state.basket;

  return isCleaningReference(universe) && !!cleaningPrestation?.is_recurrent;
};

export const isSpotCleaningSelector = (state: { basket: BasketState }) => {
  const { cleaningPrestation, universe } = state.basket;

  return isCleaningReference(universe) && !!cleaningPrestation && !cleaningPrestation.is_recurrent;
};

export const getBasketMode = (state: { basket: BasketState }) => state.basket.mode || 'creation';

// ~~~~~~~~~~ TYPES ~~~~~~~~~~

export type TrackingCallback = (...args: any[]) => void;

// ~~~~~~~~~~ CONSTANTS ~~~~~~~~~~

export const RESET = 'BASKET_RESET';
export const RESET_APPOINTMENTS_CHILDCARE = 'BASKET_RESET_APPOINTMENTS_CHILDCARE';
export const RESET_APPOINTMENTS = 'BASKET_RESET_APPOINTMENTS';
export const RESET_PROMOTION = 'BASKET_RESET_PROMOTION';
export const UPDATE_APPOINTMENT = 'BASKET_UPDATE_APPOINTMENT';
export const ACCEPT_MIN_PRICE_SURCHARGE = 'BASKET_ACCEPT_MIN_PRICE_SURCHARGE';
export const POTENTIAL_CREATED = 'BASKET_POTENTIAL_CREATED';
export const POTENTIAL_START_CREATION = 'BASKET_POTENTIAL_START_CREATION';
export const POTENTIAL_CREATION_ERROR = 'BASKET_POTENTIAL_CREATION_ERROR';
export const POTENTIAL_VALIDATION_START = 'BASKET_POTENTIAL_VALIDATION_START';
export const POTENTIAL_VALIDATION_ERROR = 'BASKET_POTENTIAL_VALIDATION_ERROR';
export const POTENTIAL_VALIDATION_SUCCESS = 'BASKET_POTENTIAL_VALIDATION_SUCCESS';
export const POTENTIAL_VALIDATION_RESET = 'BASKET_POTENTIAL_VALIDATION_RESET';
export const POTENTIAL_UPDATE_START = 'BASKET_POTENTIAL_UPDATE_START';
export const POTENTIAL_UPDATE_SUCCESS = 'BASKET_POTENTIAL_UPDATE_SUCCESS';
export const POTENTIAL_UPDATE_ERROR = 'BASKET_POTENTIAL_UPDATE_ERROR';
export const ORDER_CREATE_CHANGE_REQUEST_START = 'BASKET_ORDER_CREATE_CHANGE_REQUEST_START';
export const ORDER_CREATE_CHANGE_REQUEST_SUCCESS = 'BASKET_ORDER_CREATE_CHANGE_REQUEST_SUCCESS';
export const ORDER_CREATE_CHANGE_REQUEST_ERROR = 'BASKET_ORDER_CREATE_CHANGE_REQUEST_ERROR';
export const FETCH_TIME_SLOTS_START = 'BASKET_FETCH_TIME_SLOTS_START';
export const FETCH_TIME_SLOTS_SUCCESS = 'BASKET_FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'BASKET_FETCH_TIME_SLOTS_ERROR';
export const MODIFY_PRESTATIONS_INITIALIZATION = 'BASKET_MODIFY_PRESTATIONS_INITIALIZATION';
export const MODIFY_APPOINTMENT_INITIALIZATION = 'BASKET_MODIFY_APPOINTMENT_INITIALIZATION';
export const INIT_CLEANING_MODIFICATION = 'BASKET_INIT_CLEANING_MODIFICATION';
export const MODIFICATION_INITIALIZATION = actionTypes('BASKET_MODIFICATION_INITIALIZATION');
export const CLEAR_MODIFICATION_ERRORS = 'BASKET_CLEAR_MODIFICATION_ERRORS';
export const FIND_NEW_PRO_START = 'BASKET_FIND_NEW_PRO_START';
export const FIND_NEW_PRO_SUCCESS = 'BASKET_FIND_NEW_PRO_SUCCESS';
export const FIND_NEW_PRO_ERROR = 'BASKET_FIND_NEW_PRO_ERROR';
export const SWITCH_PRO_CHOICES = 'BASKET_SWITCH_PRO_CHOICES';
export const UPDATE_POTENTIAL_PRO_GENDER = actionTypes('UPDATE_POTENTIAL_PRO_GENDER');

// ~~~~~~~~~~ ACTIONS ~~~~~~~~~~

export const reset = (newUniverse?: UniverseReference) => ({ type: RESET, payload: { newUniverse } });
export const resetChildcareAppointments = () => ({ type: RESET_APPOINTMENTS_CHILDCARE });
export const resetAppointments = () => ({ type: RESET_APPOINTMENTS });
export const resetPromotion = () => ({ type: RESET_PROMOTION });

export function clearModificationErrors() {
  return { type: CLEAR_MODIFICATION_ERRORS };
}

export function acceptMinPriceSurcharge() {
  return { type: ACCEPT_MIN_PRICE_SURCHARGE };
}

export const updateAppointment = (newAppointment: Moment, hasNearPro?: boolean, hasNearProToday?: boolean) => ({
  type: UPDATE_APPOINTMENT,
  payload: { newAppointment, hasNearPro, hasNearProToday },
});

export function initializePrestationsModification(booking: Booking, universe: Universe) {
  return { type: MODIFY_PRESTATIONS_INITIALIZATION, booking, universe };
}

export function initializeAppointmentModification(booking: Booking, universe: Universe) {
  return { type: MODIFY_APPOINTMENT_INITIALIZATION, booking, universe };
}

type InitializeCleaningModificationParams = {
  booking: Booking;
  shouldUpdateAllOccurrences?: boolean;
};

// The web modification funnel uses this action instead of initializeModification as
// a different logic for the modification state is needed.
// In the customer app state.basket.bookingToModify.universeAtDate is used for versioned prices/attributes.
// But in future we want to use versioned endpoints (e.g. for services, options etc) instead of
// retrieving everything from universeAtDate (legacy)
export const initializeCleaningModification = ({
  booking,
  shouldUpdateAllOccurrences = true,
}: InitializeCleaningModificationParams) => ({
  type: INIT_CLEANING_MODIFICATION,
  payload: { booking, shouldUpdateAllOccurrences },
});

type InitializeModificationParams = {
  booking: Booking;
  universe: Universe;
  recurrenceOrderPublicId?: RecurrenceOrder['public_id'];
  shouldUpdateAllOccurrences?: boolean;
};

export const initializeModification =
  ({ booking, universe, recurrenceOrderPublicId, shouldUpdateAllOccurrences = true }: InitializeModificationParams) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: MODIFICATION_INITIALIZATION.START });

    if (recurrenceOrderPublicId) {
      try {
        const result = await api.orders.get(recurrenceOrderPublicId);
        dispatch({
          type: MODIFICATION_INITIALIZATION.SUCCESS,
          payload: { booking: result.data, universe, shouldUpdateAllOccurrences },
        });
      } catch (error) {
        dispatchError(dispatch, MODIFICATION_INITIALIZATION.ERROR, error);
      }
    } else {
      dispatch({
        type: MODIFICATION_INITIALIZATION.SUCCESS,
        payload: { booking, universe, shouldUpdateAllOccurrences },
      });
    }
  };

export const switchProChoices = (toRemove: ProChoiceValue, toAdd: ProChoiceValue) => ({
  type: SWITCH_PRO_CHOICES,
  payload: { toRemove, toAdd },
});

/**
 * Generates a potential object from the redux store which then usually
 * gets sent down as parameter for requests to the backend.
 *
 * @param {*} state the redux state
 *
 * @return the generated potential object
 */
type GeneratePotentialParams = {
  state: CustomerRootState;
  hasMembership: boolean;
  config: Config;
  shouldSendPrices?: boolean;
};

// For cleaning web, we now get the prices thanks to the backend,
// That's why we don't want to send the prices inside the potential. It will be calculated directly by the backend.
// For other universes or in cleaning mobile, we still send the prices (shouldSendPrices is true by default).
export const generatePotential = ({
  state,
  hasMembership,
  config,
  shouldSendPrices = true,
}: GeneratePotentialParams) => {
  const { basket } = state;
  const {
    universe,
    prosSelection,
    isInstantTaxCreditSelected,
    updateAllOccurrences,
    isBookingForSelf,
    freeParking,
    bookingToModify,
    isMembershipRequested,
    mode,
    abtest,
  } = basket;
  const previousBooking = bookingToModify?.current;

  const isSpotCleaning = isSpotCleaningSelector(state);

  const potential = {
    ...buildBasketPotential({
      basket,
      prices: shouldSendPrices ? getBasketPrices(state, config) : null,
      hasMembership,
    }),
    booking_for_self: isBookingForSelf,
    free_parking: freeParking,
    abtest_highlight_best_slots: isCleaningReference(universe) ? abtest?.highlightBestSlotsExposed : undefined,
    abtest_hide_classic_spot_cleaning_exposed_variant: isCleaningReference(universe)
      ? abtest?.hideClassicSpotCleaningExposed
      : undefined,
  };

  if (isWellnessReference(universe) && isMembershipRequested && mode === 'creation') {
    potential.membership_requested = true;
  }

  if (isSpotCleaning && isMembershipRequested && mode === 'creation') {
    potential.membership_requested = true;
  }

  potential.pro_choices_attributes = getAllProChoices(state).map(({ id, value, source }) => ({
    pro_account_id: id,
    choice: value,
    source,
  }));

  // The backend wants this field set only in the case that we actually
  // had shown the pro choice selection screen to the user (i.e. the API request
  // has been executed at least once).
  if (prosSelection) {
    potential.selected_pros = prosSelection;
  }

  if (isCleaningReference(universe)) {
    potential.instant_tax_credit_interested = isInstantTaxCreditSelected;
  }

  /**
   * When creating an order change request for a recurrent cleaning, we need to set this
   * flag to true if we want to update all following recurrences included and not just
   * this one.
   */
  if (
    'type' in previousBooking &&
    bookingUtils.isRecurringCleaning(previousBooking) &&
    previousBooking.type === 'order'
  ) {
    potential.update_recurrence = updateAllOccurrences;
  }

  return potential;
};

/**
 * Fetches the closed_time_slots, min_appointment and min_appointment_delay depending on the
 * selected prestations.
 *
 * @param {Moment} appointmentDate If provided, only the closed time slots for this date will
 * be fetched. Otherwise, the API will return the closed time slots of the next 48 hours by
 * default.
 * "next 48 hours" : next 48 hours after the time of the API call. The min_appointment that
 * is returned is a consequence of these closed time slots and therefore is not necessarily
 * today, it can be tomorrow (or even the day after tomorrow, but it is a rather edge case)
 */
export const fetchTimeSlots =
  (appointmentDate?: Moment) => async (dispatch: Dispatch, getState: () => CustomerRootState) => {
    const state = getState();
    const {
      basket: { address, proGender, universe, weekly_recurrence: weeklyRecurrence, proChoices },
    } = state;
    const zipCode = address?.zip_code;
    const prestations = getAllPrestationsInBasket(state);
    const wantedProChoices = getWantedProChoices(state);

    dispatch({ type: FETCH_TIME_SLOTS_START });

    try {
      const result = await api.timeSlots.get({
        // We expect the zip code to be defined when this method is called as the API requires it
        zipCode: zipCode!,
        universe,
        date: appointmentDate ? appointmentDate.format() : undefined,
        prestations: prestations.length ? prestations : undefined,
        proGender: proGender ?? undefined,
        weeklyRecurrence: isCleaningReference(universe) && isNumber(weeklyRecurrence) ? weeklyRecurrence : undefined,
        exclusivePro: areExclusive(proChoices),
        proChoiceSources: wantedProChoices.map(({ source }: ProChoice) => source),
      });
      dispatch({ type: FETCH_TIME_SLOTS_SUCCESS, payload: result });
    } catch (error) {
      dispatchError(dispatch, FETCH_TIME_SLOTS_ERROR, error);
    }
  };

export const setAppointmentDate =
  (appointmentDate: Moment) =>
  (dispatch: ThunkDispatch<CustomerRootState, void, Action>, getState: () => CustomerRootState) => {
    dispatch({ type: SET_ATTRIBUTE, payload: { appointmentDate } });

    const minimumAppointment = getMinimumAppointment(getState());

    /**
     * We only fetch the time slots of the appointment date if it is within 4 days after
     * the next 48 hours, or after the minimum appointment if it is after the next 48 hours.
     * (For example, the minimum appointment can be after the next 48 hours during a lockdown).
     */
    const dateToFetchTimeSlotsFrom = moment.max(
      minimumAppointment.clone().startOf('day'),
      moment().add(2, 'day').startOf('day'),
    );

    if ([0, 1, 2, 3].includes(appointmentDate.startOf('day').diff(dateToFetchTimeSlotsFrom, 'day'))) {
      dispatch(fetchTimeSlots(appointmentDate));
    }
  };

// In the case of the prices sent by the backend, we don't want to send the prices on the potential
// This new behaviour is used only for cleaning web for now
// For other universes or in cleaning mobile, we still send the prices and shouldSendPrices is true by default
export type PotentialFunctionParams = {
  hasMembership: boolean;
  trackingCallback?: TrackingCallback;
  config: Config;
  shouldSendPrices?: boolean;
};

export const validatePotential =
  ({ hasMembership, config, shouldSendPrices }: Omit<PotentialFunctionParams, 'trackingCallback'>) =>
  async (dispatch: Dispatch, getState: () => CustomerRootState) => {
    dispatch({ type: POTENTIAL_VALIDATION_START });
    try {
      const result = await api.potentials.validate(
        generatePotential({ state: getState(), hasMembership, config, shouldSendPrices }),
      );
      dispatch({ type: POTENTIAL_VALIDATION_SUCCESS, payload: result });
    } catch (error) {
      dispatchError(dispatch, POTENTIAL_VALIDATION_ERROR, error);
    }
  };

export const resetPotentialValidation = () => ({ type: POTENTIAL_VALIDATION_RESET });

export const createPotential =
  ({ hasMembership, config, trackingCallback, shouldSendPrices }: PotentialFunctionParams) =>
  async (dispatch: Dispatch, getState: () => CustomerRootState) => {
    dispatch({ type: POTENTIAL_START_CREATION });
    try {
      const potential: Booking = await api.potentials.create(
        generatePotential({
          state: getState(),
          hasMembership,
          config,
          shouldSendPrices,
        }),
      );
      dispatch({ type: POTENTIAL_CREATED, potential });

      if (trackingCallback) trackingCallback(potential);

      return dispatch({
        type: userDuck.UPDATE_LAST_POTENTIAL_ZIP_CODE,
        payload: { zipCode: potential.zip_code },
      });
    } catch (error) {
      return dispatchError(dispatch, POTENTIAL_CREATION_ERROR, error);
    }
  };

export const updatePotential =
  ({ hasMembership, config, trackingCallback, shouldSendPrices }: PotentialFunctionParams) =>
  async (dispatch: Dispatch, getState: () => CustomerRootState) => {
    const state = getState();
    const previousBooking = getPreviousBooking(state);
    const potentialPublicId = 'public_id' in previousBooking ? previousBooking.public_id : undefined;

    dispatch({ type: POTENTIAL_UPDATE_START });
    try {
      // We expect the previous booking to be defined when updatePotential is called
      const result = await api.potentials.update(
        potentialPublicId!,
        generatePotential({ state, hasMembership, config, shouldSendPrices }),
      );
      if (trackingCallback) trackingCallback(result.data);
      return dispatch({ type: POTENTIAL_UPDATE_SUCCESS, payload: result });
    } catch (error) {
      return dispatchError(dispatch, POTENTIAL_UPDATE_ERROR, error);
    }
  };

export const createOrderChangeRequest =
  ({ hasMembership, config, trackingCallback, shouldSendPrices }: PotentialFunctionParams) =>
  async (dispatch: Dispatch, getState: () => CustomerRootState) => {
    const state = getState();
    const previousBooking = getPreviousBooking(state);
    const orderId = 'public_id' in previousBooking ? previousBooking.public_id : undefined;
    dispatch({ type: ORDER_CREATE_CHANGE_REQUEST_START });
    try {
      // We expect the previous booking to be defined when createOrderChangeRequest is called
      const result = await api.orders.createChangeRequest(
        orderId!,
        generatePotential({ state, hasMembership, config, shouldSendPrices }),
      );
      if (trackingCallback) trackingCallback(result.data.order);
      return dispatch({ type: ORDER_CREATE_CHANGE_REQUEST_SUCCESS, payload: result });
    } catch (error) {
      return dispatchError(dispatch, ORDER_CREATE_CHANGE_REQUEST_ERROR, error);
    }
  };

export const findNewPro =
  ({ publicId, cancellationReason, reasonDetail, origin }: api.FindNewProArgs) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: FIND_NEW_PRO_START });
    try {
      const result = await api.potentials.findNewPro({ publicId, cancellationReason, reasonDetail, origin });
      dispatch({ type: FIND_NEW_PRO_SUCCESS, payload: result });
    } catch (error) {
      dispatchError(dispatch, FIND_NEW_PRO_ERROR, error);
    }
  };

export const updatePotentialProGender =
  ({ publicId, proGender }: api.UpdatePotentialProGenderArgs) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: UPDATE_POTENTIAL_PRO_GENDER.START });
    try {
      const result = await api.potentials.updatePotentialProGender({ publicId, proGender });
      dispatch({ type: UPDATE_POTENTIAL_PRO_GENDER.SUCCESS, payload: result });
    } catch (error) {
      dispatchError(dispatch, UPDATE_POTENTIAL_PRO_GENDER.ERROR, error);
    }
  };

// ~~~~~~~~~~ REDUCER ~~~~~~~~~~

export const initialState: BasketState = {
  ...commonInitialState,
  /**
   * mode is common to pro and customer app, it is part of commonInitialState.
   * Only the customer app has an initial value.
   */
  mode: 'creation',
  proChoices: [],
  minPriceSurchargeAccepted: false,
  potentialValidation: {
    onGoing: false,
    errors: undefined,
    success: false,
    skipPreauthorization: null,
    stripeClientSecret: null,
    warnings: [],
  },
  potentialUpdate: {
    onGoing: false,
    errors: undefined,
    result: null,
  },
  potentialCreation: {
    onGoing: false,
    errors: {},
    result: null,
    requiresAction: false,
    paymentIntentClientSecret: undefined,
  },
  paymentInfos: {
    stripePaymentMethodId: undefined,
    stripePaymentIntentId: undefined,
    stripeSetupIntentId: undefined,
    preauthorizationSkipped: undefined,
  },
  orderChangeRequest: {
    onGoing: false,
    errors: undefined,
    result: null,
  },
  bookingToModify: { ...object.fetchInitialState, universeAtDate: undefined },
  updateAllOccurrences: true,
  newPro: { ...object.updateInitialState },
  referringProParams: undefined,
  prosSelection: undefined,
  isInstantTaxCreditSelected: undefined,
  isBookingForSelf: undefined,
  freeParking: undefined,
  isMembershipRequested: undefined,
  isStaled: false,
  abtest: undefined,
};

type OnResetPayload = { newUniverse: UniverseReference };

const onReset = (state: BasketState, action: PayloadAction<OnResetPayload>) => {
  const { newUniverse } = action.payload;
  const universe = newUniverse || state.universe;

  return {
    ...initialState,
    universe,
  };
};

const onAppointmentsReset = (state: BasketState) => ({
  ...state,
  appointment: { ...initialState.appointment },
  appointmentDate: initialState.appointmentDate,
});

const onPromotionReset = (state: BasketState) => ({
  ...state,
  promotion: initialState.promotion,
});

const onChildcareAppointmentsReset = (state: BasketState) => ({
  ...state,
  appointment: { ...initialState.appointment },
  duration: null,
  endsAt: null,
  schedules: [],
  includeHolidays: false,
});

const onClearModificationErrors = (state: BasketState) => ({
  ...state,
  potentialUpdate: {
    ...state.potentialUpdate,
    errors: undefined,
  },
  orderChangeRequest: {
    ...state.orderChangeRequest,
    errors: undefined,
  },
});

type OnUpdateAppointmentPayload = {
  newAppointment: Moment;
  hasNearPro?: boolean;
  hasNearProToday?: boolean;
};
const onUpdateAppointment = (state: BasketState, action: PayloadAction<OnUpdateAppointmentPayload>) => {
  const { newAppointment, hasNearPro, hasNearProToday } = action.payload;
  const { appointment, fieldsToUpdate } = updateAppointmentUtil(
    state.appointment,
    newAppointment,
    hasNearPro,
    hasNearProToday,
  );

  return {
    ...state,
    appointment: appointment || initialState.appointment,
    ...fieldsToUpdate,
  };
};

const onAcceptMinPriceSurcharge = (state: BasketState) => ({ ...state, minPriceSurchargeAccepted: true });

const potentialStartCreation = (state: BasketState) => ({
  ...state,
  potentialCreation: {
    ...state.potentialCreation,
    onGoing: true,
    errors: {},
    result: null,
  },
});

type PotentialPayload = { potential: PayloadPotential };

const potentialCreated = (state: BasketState, action: PotentialPayload) => {
  const result = bookingMapper<PayloadPotential>(action.potential);

  return {
    ...state,
    potentialCreation: {
      ...state.potentialCreation,
      onGoing: false,
      errors: {},
      result,
    },
  };
};

type PotentialCreationErrorPayload = {
  errors: PotentialCreation['errors'];
  response: {
    requires_action: PotentialCreation['requiresAction'];
    payment_intent_client_secret: PotentialCreation['paymentIntentClientSecret'];
  };
};
const potentialCreationError = (state: BasketState, action: PayloadAction<PotentialCreationErrorPayload>) => {
  const { errors, response } = action.payload;
  return {
    ...state,
    potentialCreation: {
      ...state.potentialCreation,
      onGoing: false,
      errors: errors instanceof Array ? { potential: errors } : errors,
      requiresAction: response && response.requires_action,
      paymentIntentClientSecret: response && response.payment_intent_client_secret,
      result: null,
    },
  };
};

const potentialStartValidation = (state: BasketState) => ({
  ...state,
  potentialValidation: {
    ...state.potentialValidation,
    onGoing: true,
    errors: undefined,
    success: false,
    warnings: [],
    skipPreauthorization: null,
    stripeClientSecret: null,
  },
});

type PotentialValidationErrorPayload = {
  errors: PotentialValidation['errors'];
};
const potentialValidationError = (state: BasketState, action: PayloadAction<PotentialValidationErrorPayload>) => ({
  ...state,
  potentialValidation: {
    ...state.potentialValidation,
    onGoing: false,
    errors: action.payload.errors,
    success: false,
    warnings: [],
    skipPreauthorization: null,
    stripeClientSecret: null,
  },
});

type PotentialValidationSuccessPayload = {
  skip_preauthorization: PotentialValidation['skipPreauthorization'];
  stripe_client_secret: PotentialValidation['stripeClientSecret'];
  warnings: PotentialValidation['warnings'];
};
const potentialValidated = (state: BasketState, action: PayloadAction<PotentialValidationSuccessPayload>) => ({
  ...state,
  potentialValidation: {
    ...state.potentialValidation,
    onGoing: false,
    errors: undefined,
    success: true,
    skipPreauthorization: action.payload.skip_preauthorization,
    stripeClientSecret: action.payload.stripe_client_secret,
    warnings: action.payload.warnings || [],
  },
});

const onPotentialValidationReset = (state: BasketState) => ({
  ...state,
  paymentInfos: {
    ...initialState.paymentInfos,
  },
  potentialCreation: {
    ...initialState.potentialCreation,
  },
  potentialValidation: {
    ...initialState.potentialValidation,
  },
});

const potentialStartUpdate = (state: BasketState) => ({
  ...state,
  potentialUpdate: {
    ...state.potentialUpdate,
    onGoing: true,
    errors: undefined,
    result: null,
  },
});

type PotentialUpdateErrorPayload = {
  errors: PotentialUpdate['errors'];
};
const potentialUpdateError = (state: BasketState, action: PayloadAction<PotentialUpdateErrorPayload>) => ({
  ...state,
  potentialUpdate: {
    ...state.potentialUpdate,
    onGoing: false,
    errors: action.payload.errors,
    result: null,
  },
});

type PotentialUpdateSuccessPayload = {
  data: Booking;
};
const potentialUpdated = (state: BasketState, action: PayloadAction<PotentialUpdateSuccessPayload>) => ({
  ...state,
  potentialUpdate: {
    ...state.potentialUpdate,
    onGoing: false,
    errors: undefined,
    result: bookingMapper<Booking>(action.payload.data),
  },
});

const orderStartChangeRequest = (state: BasketState) => ({
  ...state,
  orderChangeRequest: {
    ...state.orderChangeRequest,
    onGoing: true,
    errors: undefined,
    result: null,
  },
});

type ChangeRequestErrorPayload = {
  errors: OrderChangeRequest['errors'];
};
const orderChangeRequestError = (state: BasketState, action: PayloadAction<ChangeRequestErrorPayload>) => ({
  ...state,
  orderChangeRequest: {
    ...state.orderChangeRequest,
    onGoing: false,
    errors: action.payload.errors,
    result: null,
  },
});

type ChangeRequestSuccessPayload = {
  data: {
    order: Booking;
  };
};
const orderChangeRequestCreated = (state: BasketState, action: PayloadAction<ChangeRequestSuccessPayload>) => ({
  ...state,
  orderChangeRequest: {
    ...state.orderChangeRequest,
    onGoing: false,
    errors: undefined,
    result: bookingMapper<Booking>(action.payload.data.order),
  },
});

const onFetchTimeSlotsStart = (state: BasketState) => ({
  ...state,
  closedTimeSlots: onFetchStart(state.closedTimeSlots),
});

/**
 * - When we fetch regular closed time slots (i.e. closed time slots starting from now),
 * we receive the closed time slots for the next 48 hours only.
 * - When we fetch closed time slots providing a date param (@see fetchTimeSlots function),
 * we receive the closed time slots for the provided date only.
 *
 * Everytime we fetch closed time slots (providing a date param or not), we remove the
 * closed time slots that were previously fetched providing a date param
 * to make sure we only keep up-to-date closed time slots in our state.
 */
const mergeClosedTimeSlots = (all: Moment[], latestFetched: Moment[]) =>
  all.filter(isWithin48Hours).concat(latestFetched);

type FetchTimeSlotsSuccessPayload = {
  data: {
    closed_time_slots: string[];
    min_appointment_delay: number;
    min_appointment: string;
  };
};
const onFetchTimeSlotsSuccess = (
  state: BasketState,
  { payload: { data } }: PayloadAction<FetchTimeSlotsSuccessPayload>,
) => ({
  ...state,
  closedTimeSlots: {
    ...initialState.closedTimeSlots,
    isLoaded: true,
    /**
     * When fetching the closed time slots depending on the date,
     * the backend will only compute closed time slots for this specific day,
     * without recomputing min_appointment and min_appointment_delay.
     * In this case, it will return min_appointment and min_appointment_delay as null,
     * so we should keep the existing values.
     */
    minAppointment: data.min_appointment ? moment(data.min_appointment) : state.closedTimeSlots.minAppointment,
    minAppointmentDelay: data.min_appointment_delay
      ? data.min_appointment_delay
      : state.closedTimeSlots.minAppointmentDelay,
    all: mergeClosedTimeSlots(
      state.closedTimeSlots.all,
      data.closed_time_slots.map((slot) => moment(slot)),
    ),
    fetchedAt: moment(),
  },
});

const onFetchTimeSlotsError = (state: BasketState, action: PayloadAction<ErrorPayload>) => ({
  ...state,
  closedTimeSlots: onFetchError(state.closedTimeSlots, action),
});

type InitPrestationsModificationPayload = {
  booking: Booking;
  universe: Universe;
};

const onInitPrestationsModification = (
  state: BasketState,
  action: InitPrestationsModificationPayload,
): BasketState => {
  const { universe, booking } = action;
  return {
    ...state,
    universe: universe.reference,
    mode: 'modifyPrestations',
    bookingToModify: {
      ...state.bookingToModify,
      isLoaded: true,
      isLoading: false,
      current: booking,
      universeAtDate: universe,
    },
    ...createBasketFromBooking(booking, universe),
  };
};

type InitAppointmentModificationPayload = {
  booking: Booking;
  universe: Universe;
};
const onInitAppointmentModification = (
  state: BasketState,
  action: InitAppointmentModificationPayload,
): BasketState => {
  const { universe, booking } = action;
  return {
    ...state,
    universe: universe.reference,
    mode: 'modifyAppointment',
    bookingToModify: {
      ...state.bookingToModify,
      isLoaded: true,
      isLoading: false,
      current: booking,
      universeAtDate: universe,
    },
    /**
     * Passing true here means we are resetting the whole appointment object.
     * We need to do this here so that the user can choose a new primary appointment
     * in the modification UI in combination with the updateAppointment utils.
     */
    ...createBasketFromBooking(booking, universe, { clearAppointments: true }),
  };
};

type InitCleaningModificationPayload = {
  booking: Booking;
  shouldUpdateAllOccurrences: boolean;
};

const onInitCleaningModification = (
  state: BasketState,
  { payload: { booking, shouldUpdateAllOccurrences } }: PayloadAction<InitCleaningModificationPayload>,
): BasketState => ({
  ...state,
  universe: booking.universe,
  mode: 'modification',
  updateAllOccurrences: shouldUpdateAllOccurrences,
  ...createCleaningBasketFromBooking(booking, { shouldUpdateAllOccurrences }),
  bookingToModify: {
    ...state.bookingToModify,
    isLoaded: true,
    isLoading: false,
    current: booking,
  },
});

const onInitModificationStart = (state: BasketState) => ({
  ...state,
  bookingToModify: object.onFetchStart(state.bookingToModify),
});

type InitModificationSuccessPayload = {
  booking: Booking;
  universe: Universe;
  shouldUpdateAllOccurrences: boolean;
};

const onInitModificationSuccess = (
  state: BasketState,
  action: PayloadAction<InitModificationSuccessPayload>,
): BasketState => {
  const { booking, universe, shouldUpdateAllOccurrences } = action.payload;
  return {
    ...state,
    universe: universe.reference,
    mode: 'modification',
    updateAllOccurrences: shouldUpdateAllOccurrences,
    ...(isCleaningReference(universe.reference)
      ? createCleaningBasketFromBooking(booking, { shouldUpdateAllOccurrences })
      : createBasketFromBooking(booking, universe, { shouldUpdateAllOccurrences })),
    bookingToModify: {
      ...state.bookingToModify,
      isLoaded: true,
      isLoading: false,
      current: booking,
      universeAtDate: universe,
    },
  };
};

type InitModificationErrorPayload = {
  errors: string[];
};
const onInitModificationError = (state: BasketState, action: PayloadAction<InitModificationErrorPayload>) => ({
  ...state,
  bookingToModify: object.onFetchError(state.bookingToModify, action),
});

const findNewProStart = (state: BasketState) => ({
  ...state,
  newPro: object.onUpdateStart(state.newPro),
});

type FindNewProSuccessPayload = {
  data: Booking;
};
const findNewProSuccess = (state: BasketState, action: PayloadAction<FindNewProSuccessPayload>) => ({
  ...state,
  newPro: object.onUpdateSuccessWithMapper(bookingMapper)(state.newPro, action),
});

type FindNewProErrorPayload = {
  errors: string[];
};
const findNewProError = (state: BasketState, action: PayloadAction<FindNewProErrorPayload>) => ({
  ...state,
  newPro: object.onUpdateError(state.newPro, action),
});

type SwitchProChoicesPayload = {
  toRemove: ProChoiceValue;
  toAdd: ProChoiceValue;
};
const onSwitchProChoices = (
  state: BasketState,
  { payload: { toRemove, toAdd } }: PayloadAction<SwitchProChoicesPayload>,
) => ({
  ...state,
  proChoices: switchChoices(state.proChoices, toRemove, toAdd),
});

const updatePotentialProGenderStart = (state: BasketState) => ({
  ...state,
  newPro: object.onUpdateStart(state.newPro),
});

type UpdatePotentialProGenderSuccessPayload = {
  data: Booking;
};
const updatePotentialProGenderSuccess = (
  state: BasketState,
  action: PayloadAction<UpdatePotentialProGenderSuccessPayload>,
) => ({
  ...state,
  newPro: object.onUpdateSuccessWithMapper(bookingMapper)(state.newPro, action),
});

type UpdatePotentialProGenderErrorPayload = {
  errors: string[];
};
const updatePotentialProGenderError = (
  state: BasketState,
  action: PayloadAction<UpdatePotentialProGenderErrorPayload>,
) => ({
  ...state,
  newPro: object.onUpdateError(state.newPro, action),
});

const handlers = {
  ...commonHandlers,
  [RESET]: onReset,
  [RESET_APPOINTMENTS_CHILDCARE]: onChildcareAppointmentsReset,
  [RESET_APPOINTMENTS]: onAppointmentsReset,
  [RESET_PROMOTION]: onPromotionReset,
  [CLEAR_MODIFICATION_ERRORS]: onClearModificationErrors,
  [UPDATE_APPOINTMENT]: onUpdateAppointment,
  [ACCEPT_MIN_PRICE_SURCHARGE]: onAcceptMinPriceSurcharge,
  [POTENTIAL_START_CREATION]: potentialStartCreation,
  [POTENTIAL_CREATED]: potentialCreated,
  [POTENTIAL_CREATION_ERROR]: potentialCreationError,
  [POTENTIAL_VALIDATION_START]: potentialStartValidation,
  [POTENTIAL_VALIDATION_ERROR]: potentialValidationError,
  [POTENTIAL_VALIDATION_SUCCESS]: potentialValidated,
  [POTENTIAL_VALIDATION_RESET]: onPotentialValidationReset,
  [POTENTIAL_UPDATE_START]: potentialStartUpdate,
  [POTENTIAL_UPDATE_ERROR]: potentialUpdateError,
  [POTENTIAL_UPDATE_SUCCESS]: potentialUpdated,
  [ORDER_CREATE_CHANGE_REQUEST_START]: orderStartChangeRequest,
  [ORDER_CREATE_CHANGE_REQUEST_ERROR]: orderChangeRequestError,
  [ORDER_CREATE_CHANGE_REQUEST_SUCCESS]: orderChangeRequestCreated,
  [FETCH_TIME_SLOTS_START]: onFetchTimeSlotsStart,
  [FETCH_TIME_SLOTS_SUCCESS]: onFetchTimeSlotsSuccess,
  [FETCH_TIME_SLOTS_ERROR]: onFetchTimeSlotsError,
  [MODIFY_PRESTATIONS_INITIALIZATION]: onInitPrestationsModification,
  [MODIFY_APPOINTMENT_INITIALIZATION]: onInitAppointmentModification,
  [INIT_CLEANING_MODIFICATION]: onInitCleaningModification,
  [MODIFICATION_INITIALIZATION.START]: onInitModificationStart,
  [MODIFICATION_INITIALIZATION.SUCCESS]: onInitModificationSuccess,
  [MODIFICATION_INITIALIZATION.ERROR]: onInitModificationError,
  [FIND_NEW_PRO_START]: findNewProStart,
  [FIND_NEW_PRO_ERROR]: findNewProError,
  [FIND_NEW_PRO_SUCCESS]: findNewProSuccess,
  [UPDATE_POTENTIAL_PRO_GENDER.START]: updatePotentialProGenderStart,
  [UPDATE_POTENTIAL_PRO_GENDER.SUCCESS]: updatePotentialProGenderSuccess,
  [UPDATE_POTENTIAL_PRO_GENDER.ERROR]: updatePotentialProGenderError,
  [SWITCH_PRO_CHOICES]: onSwitchProChoices,
};

export const reducer = exporter<BasketState>(initialState, handlers);
